// /book/:providerId — a partner's own booking page. Also what the website widget opens (?embed=1).
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Star, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const SITE = typeof window !== "undefined" ? window.location.origin : "";
const money = (v, unit) => (v == null || v === "" ? "" : "£" + Number(v).toFixed(2).replace(/\.00$/, "") + (unit ? " / " + unit : ""));

export default function BookPage() {
  const { providerId } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api("/widget/" + encodeURIComponent(providerId), { auth: false })
      .then((x) => { setD(x); document.title = "Book " + x.business.name + " · Fable+"; })
      .catch((e) => setErr(e.message || "This booking page isn't available."));
  }, [providerId]);
  const open = (id) => {
    const url = SITE + "/listing/" + id;
    if (embed) window.open(url, "_blank", "noopener");
    else window.location.assign(url);
  };
  if (err) return <main className="mx-auto max-w-xl px-4 py-16 text-center"><h1 className="text-2xl font-bold">Booking page unavailable</h1><p className="mt-2 text-muted-foreground">{err}</p></main>;
  if (!d) return <main className="mx-auto max-w-3xl px-4 py-10"><div className="h-24 animate-pulse rounded-2xl bg-muted" /><div className="mt-4 grid gap-3 sm:grid-cols-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-muted" />)}</div></main>;
  const b = d.business;
  return (
    <main className={"mx-auto max-w-3xl px-4 " + (embed ? "py-4" : "py-10")} data-testid="book-page">
      <header className="flex items-center gap-4">
        {b.avatar ? <img src={b.avatar} alt="" className="h-14 w-14 rounded-full object-cover" style={{ boxShadow: "0 0 0 3px " + b.color }} /> :
          <div className="flex h-14 w-14 items-center justify-center rounded-full text-xl font-bold text-white" style={{ background: b.color }}>{b.name.charAt(0)}</div>}
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold">{b.name}</h1>
          {b.location && <p className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{b.location}</p>}
        </div>
      </header>
      {b.bio && <p className="mt-4 max-w-2xl text-sm text-muted-foreground">{b.bio}</p>}
      <h2 className="mt-8 text-sm font-semibold text-muted-foreground">Choose what to book</h2>
      <ul className="mt-3 grid gap-3 sm:grid-cols-2">
        {d.listings.map((l) => (
          <li key={l.id}>
            <button type="button" onClick={() => open(l.id)} className="flex w-full gap-3 rounded-2xl border bg-card p-3 text-left transition hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="book-item">
              {l.image_url ? <img src={l.image_url} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" loading="lazy" /> : <div className="h-20 w-20 shrink-0 rounded-xl" style={{ background: b.color, opacity: 0.15 }} />}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{l.title}</span>
                {l.subtitle && <span className="block truncate text-xs text-muted-foreground">{l.subtitle}</span>}
                <span className="mt-1 flex items-center gap-2 text-sm">
                  <span className="font-semibold">{money(l.price, l.price_unit)}</span>
                  {l.rating ? <span className="flex items-center gap-0.5 text-xs text-muted-foreground"><Star className="h-3 w-3 fill-current" />{Number(l.rating).toFixed(1)}{l.review_count ? " (" + l.review_count + ")" : ""}</span> : null}
                </span>
                <span className="mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: b.color }}>Book</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {!d.listings.length && <p className="mt-6 rounded-2xl border p-6 text-center text-sm text-muted-foreground">Nothing to book here yet.</p>}
      <p className="mt-8 text-center text-xs text-muted-foreground">Bookings and payments by <a href={SITE} target={embed ? "_blank" : undefined} rel="noopener noreferrer" className="font-semibold underline">Fable+</a></p>
    </main>
  );
}
